import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FlaskConical, Loader2, CheckCircle2 } from "lucide-react";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin"); // signin | request
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const switchMode = (m) => { setMode(m); setError(""); setSuccess(""); };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSuccess(""); setLoading(true);
    try {
      if (mode === "signin") {
        await login(email, password);
        navigate("/substances");
      } else {
        const res = await register({ name, email, password });
        setSuccess(res?.message || "Access request submitted. An administrator will review your account.");
        setName(""); setPassword("");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm animate-fade-up">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <div className="h-9 w-9 rounded-md bg-foreground flex items-center justify-center"><FlaskConical className="h-5 w-5 text-background" /></div>
          <span className="font-heading font-bold tracking-tight text-lg">Conscious Basel</span>
        </div>

        <h1 className="font-heading text-xl font-semibold mb-1">
          {mode === "signin" ? "Employee sign in" : "Request access"}
        </h1>
        <p className="text-sm text-muted-foreground mb-6">
          {mode === "signin"
            ? "Internal research database — authorized staff only."
            : "New here? Request an account and an admin will approve your access."}
        </p>

        {success ? (
          <div className="text-sm bg-emerald-500/10 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 rounded-md px-3 py-3 flex items-start gap-2" data-testid="register-success">
            <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{success}</span>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {error && <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2" data-testid="login-error">{error}</div>}
            {mode === "request" && (
              <div className="space-y-1.5"><Label className="text-xs">Full name</Label><Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name" /></div>
            )}
            <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" /></div>
            <Button type="submit" className="w-full" disabled={loading} data-testid={mode === "signin" ? "login-submit" : "register-submit"}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (mode === "signin" ? "Sign in" : "Request access")}
            </Button>
          </form>
        )}

        <div className="mt-6 text-center text-sm text-muted-foreground">
          {mode === "signin" ? (
            <button type="button" className="hover:text-foreground transition-colors" onClick={() => switchMode("request")} data-testid="switch-to-request">
              Don't have an account? <span className="text-foreground font-medium underline underline-offset-4">Request access</span>
            </button>
          ) : (
            <button type="button" className="hover:text-foreground transition-colors" onClick={() => switchMode("signin")} data-testid="switch-to-signin">
              Already have access? <span className="text-foreground font-medium underline underline-offset-4">Sign in</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
